/*
 * Copyright 2022 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 */

import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Menu, {MenuItem} from '@amzn/meridian/menu';
import globeTokens from '@amzn/meridian-tokens/base/icon/globe';
import {useLocalizationContext} from '@amzn/react-arb-tools';
import _ from 'lodash';
import React from 'react';

import {localizationContextBuilder} from '../../../app';
import {getLocalStorageItem, LocalStorageKeys, setLocalStorageItem} from '../../../utility/local-storage';

type LanguageOption = { label: string, value: string };

export const DEFAULT_LANGUAGE_VALUE = 'en-US';

const LanguageMenu = () => {

    const { setLocalizationContext } = useLocalizationContext();

    // This language list should be kept up-to-date with CXIds "associatetools.ihm.amazon.dev" for operations
    // https://w.amazon.com/bin/view/INTech/TranslationServices/TranslationIngestion/API/TRANSCOMGuide/CXID/#HCXIdsforoperations
    const languageList: LanguageOption[] = [
        {label: 'English (United States)', value: DEFAULT_LANGUAGE_VALUE},
        {label: 'العربية (الإمارات العربية المتحدة)', value: 'ar-AE'},
        {label: 'Deutsch', value: 'de-DE'},
        {label: 'English (United Kingdom)', value: 'en-GB'},
        {label: 'Español (España)', value: 'es-ES'},
        {label: 'Français (France)', value: 'fr-FR'},
        {label: 'Italiano', value: 'it-IT'},
        {label: '日本語', value: 'ja-JP'},
        {label: 'Polski', value: 'pl-PL'},
    ];

    const selectedLanguageValue = getLocalStorageItem(LocalStorageKeys.ID4_LANGUAGE) ?? DEFAULT_LANGUAGE_VALUE;
    const selectedLanguageLabel = languageList.find(option => option.value === selectedLanguageValue).label;

    const [languageLabel, setLanguageLabel] = React.useState(selectedLanguageLabel);
    
    const buttonRef = React.useRef();
    const [open, setOpen] = React.useState(false);
    const onClickButton = React.useCallback(() => setOpen(!open), []);
    const onClose = React.useCallback(() => setOpen(false), []);

    const setChosenLanguage = (option: LanguageOption) => {
        // Store the language in local storage so it can be retrieved next time the page loads
        setLocalStorageItem(LocalStorageKeys.ID4_LANGUAGE, option.value);
        // Store is a React state variable in order to re-render the component so it shows the changed language
        setLanguageLabel(option.label);
        // Update the ARB context with the new locale value (this will load the new the language files)
        setLocalizationContext(
            localizationContextBuilder
                .withLocale(option.value)
                .build()
        );
    };

    return (
        <React.Fragment>
            <Button type='icon' size='small' onClick={onClickButton} ref={buttonRef}>
                <Icon tokens={globeTokens}>Language</Icon>
                {languageLabel}
            </Button>
            <Menu anchorNode={buttonRef.current} open={open} position='bottom' onClose={onClose} minWidth={175}>
                {
                    _.sortBy(languageList, 'value').map(lang => {
                        return (
                            <MenuItem
                                key={`region-menu-item-${lang.value}`}
                                onClick={() => setChosenLanguage(lang)}
                            >
                                {lang.label}
                            </MenuItem>
                        );
                    })
                }
            </Menu>
        </React.Fragment>
    );
};

export default LanguageMenu;
